import React from "react";
import { Link } from "react-router-dom";
import {
    Search, Filter, RotateCcw,
    CheckSquare, Square, Tag, Truck, Zap, Heart, BookmarkPlus,
    Grid3X3, List, X, Package
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Slider } from "../../components/ui/slider";
import { Button } from "../../components/ui/button";
import { FilterDropdown } from "./CategorySubcategories";

const SORT_OPTIONS = [
    { value: "created_at_desc", label: "Nejnovější" },
    { value: "price_asc", label: "Cena: nejlevnější" },
    { value: "price_desc", label: "Cena: nejdražší" },
    { value: "name_asc", label: "Název A-Z" },
    { value: "discount_desc", label: "Největší sleva" },
];
const PAGE_LIMITS = [24, 48, 96];

export function FilterBar({
    search, setSearch, sort, setSort,
    priceRange, setPriceRange,
    catBrands, selectedBrands, toggleBrand,
    inStockOnly, setInStockOnly, onSaleOnly, setOnSaleOnly,
    paramOptions, selectedParams, toggleParam,
    activeParamCount, hasFilters, clearFilters,
    viewMode, handleViewModeChange,
    pageLimit, setPageLimit, setPage,
    setFiltersOpen,
    isLoggedIn, savedFilters,
    showSaveFilter, setShowSaveFilter,
    saveFilterName, setSaveFilterName,
    handleSaveFilter, handleLoadFilter, handleDeleteFilter
}) {
    return (
        <div className="bg-white rounded-2xl border border-gray-200/80 p-3 sm:p-4 mb-6 shadow-sm">
            <div className="flex items-center gap-2.5 flex-wrap">
                <div className="relative flex-1 min-w-40">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Hledat v kategorii..."
                        className="pl-9 h-9 text-sm rounded-full border-gray-200 focus:border-green-400"
                        data-testid="category-search-input" />
                </div>

                <div className="h-5 w-px bg-gray-200 hidden sm:block" />

                <FilterDropdown
                    label={priceRange[0] > 0 || priceRange[1] < 200000 ? `${priceRange[0].toLocaleString("cs-CZ")}-${priceRange[1] >= 200000 ? "max" : priceRange[1].toLocaleString("cs-CZ")} Kč` : "Cena"}
                    active={priceRange[0] > 0 || priceRange[1] < 200000}
                    count={priceRange[0] > 0 || priceRange[1] < 200000 ? 1 : 0}>
                    {(setOpen) => (
                        <div className="px-4 py-4 w-64">
                            <p className="text-xs font-semibold text-gray-500 mb-3">Cenový rozsah</p>
                            <Slider min={0} max={200000} step={500} value={priceRange} onValueChange={setPriceRange} className="mb-3" />
                            <div className="flex justify-between text-xs text-gray-600 font-medium">
                                <span>{priceRange[0].toLocaleString("cs-CZ")} Kč</span>
                                <span>{priceRange[1] >= 200000 ? "Bez limitu" : `${priceRange[1].toLocaleString("cs-CZ")} Kč`}</span>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={() => { setPriceRange([0, 200000]); setOpen(false); }}
                                    className="flex-1 text-xs text-gray-500 border border-gray-200 rounded-full py-1.5 hover:bg-gray-50">Vše</button>
                                <button onClick={() => setOpen(false)}
                                    className="flex-1 text-xs text-white rounded-full py-1.5 font-semibold bg-green-600 hover:bg-green-700">Použít</button>
                            </div>
                        </div>
                    )}
                </FilterDropdown>

                {catBrands.length > 1 && (
                    <FilterDropdown label="Výrobce" active={selectedBrands.length > 0} count={selectedBrands.length}>
                        {(setOpen) => (
                            <div className="p-2 w-56 max-h-72 overflow-y-auto">
                                <p className="text-xs font-semibold text-gray-500 mb-2 px-1">Vybrat výrobce</p>
                                <div className="space-y-0.5">
                                    {catBrands.map(brand => (
                                        <button key={brand} type="button" onClick={() => toggleBrand(brand)}
                                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors text-left ${selectedBrands.includes(brand) ? "bg-green-50 text-green-800 font-medium" : "text-gray-700 hover:bg-gray-50"}`}
                                            data-testid={`filter-brand-${brand}`}>
                                            {selectedBrands.includes(brand) ? <CheckSquare size={14} className="text-green-600 flex-shrink-0" /> : <Square size={14} className="text-gray-300 flex-shrink-0" />}
                                            <span className="truncate">{brand}</span>
                                        </button>
                                    ))}
                                </div>
                                {selectedBrands.length > 0 && (
                                    <button onClick={() => { toggleBrand(null); setOpen(false); }}
                                        className="w-full mt-2 text-xs text-red-500 border border-red-200 rounded-full py-1.5 hover:bg-red-50">Vymazat výrobce</button>
                                )}
                            </div>
                        )}
                    </FilterDropdown>
                )}

                <FilterDropdown label="Dostupnost" active={inStockOnly || onSaleOnly} count={(inStockOnly ? 1 : 0) + (onSaleOnly ? 1 : 0)}>
                    {(setOpen) => (
                        <div className="p-3 w-52">
                            <p className="text-xs font-semibold text-gray-500 mb-2">Dostupnost</p>
                            <div className="space-y-2">
                                <label className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded-xl hover:bg-gray-50">
                                    <Checkbox checked={inStockOnly} onCheckedChange={v => { setInStockOnly(v); if (v) setOpen(false); }} data-testid="filter-in-stock" />
                                    <div><p className="text-sm font-medium text-gray-700">Pouze skladem</p><p className="text-xs text-gray-400">Ihned k odeslání</p></div>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded-xl hover:bg-red-50">
                                    <Checkbox checked={onSaleOnly} onCheckedChange={v => { setOnSaleOnly(v); if (v) setOpen(false); }} data-testid="filter-on-sale" />
                                    <div><p className="text-sm font-medium text-red-600">Ve slevě</p><p className="text-xs text-gray-400">Aktuální akce</p></div>
                                </label>
                            </div>
                        </div>
                    )}
                </FilterDropdown>

                {paramOptions.length > 0 && (
                    <FilterDropdown label="Parametry" active={activeParamCount > 0} count={activeParamCount}>
                        {(setOpen) => (
                            <div className="p-3 w-72 max-h-80 overflow-y-auto" data-testid="param-filter-panel">
                                <p className="text-xs font-semibold text-gray-500 mb-2">Filtrovat dle parametrů</p>
                                <div className="space-y-3">
                                    {paramOptions.slice(0, 8).map(po => (
                                        <div key={po.key}>
                                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">{po.key}</p>
                                            <div className="flex flex-wrap gap-1">
                                                {po.values.slice(0, 8).map(val => (
                                                    <button key={val} onClick={() => toggleParam(po.key, val)}
                                                        className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                                                            selectedParams[po.key] === val
                                                                ? "bg-green-600 text-white font-semibold"
                                                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                        }`}
                                                        data-testid={`param-${po.key}-${val}`}>
                                                        {val}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {activeParamCount > 0 && (
                                    <button onClick={() => { Object.keys(selectedParams || {}).forEach(k => toggleParam(k, (selectedParams || {})[k])); setOpen(false); }}
                                        className="w-full mt-3 text-xs text-red-500 border border-red-200 rounded-full py-1.5 hover:bg-red-50">Vymazat parametry</button>
                                )}
                            </div>
                        )}
                    </FilterDropdown>
                )}

                <div className="h-5 w-px bg-gray-200 hidden sm:block" />

                <Select value={sort} onValueChange={v => setSort(v)}>
                    <SelectTrigger className="w-44 h-9 text-sm border-gray-200 rounded-full" data-testid="sort-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>

                <div className="flex bg-gray-100 rounded-full p-0.5" data-testid="view-mode-toggle">
                    <button onClick={() => handleViewModeChange("grid")} className={`p-1.5 rounded-full transition-all ${viewMode === "grid" ? "bg-white text-green-700 shadow-sm" : "text-gray-400"}`} title="Galerie" data-testid="view-grid-btn"><Grid3X3 size={15} /></button>
                    <button onClick={() => handleViewModeChange("list")} className={`p-1.5 rounded-full transition-all ${viewMode === "list" ? "bg-white text-green-700 shadow-sm" : "text-gray-400"}`} title="Seznam" data-testid="view-list-btn"><List size={15} /></button>
                    <button onClick={() => handleViewModeChange("b2b")} className={`px-2 py-1 rounded-full transition-all text-[10px] font-bold ${viewMode === "b2b" ? "bg-white text-green-700 shadow-sm" : "text-gray-400"}`} title="B2B kompaktni" data-testid="view-b2b-btn">B2B</button>
                </div>

                <select value={pageLimit} onChange={e => { setPageLimit(Number(e.target.value)); setPage(1); }}
                    className="h-8 px-2 rounded-full border border-gray-200 text-xs bg-white text-gray-600" data-testid="per-page-select">
                    {PAGE_LIMITS.map(n => <option key={n} value={n}>{n} / strana</option>)}
                </select>

                <button className="lg:hidden flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                    onClick={() => setFiltersOpen(true)} data-testid="mobile-filter-btn">
                    <Filter size={13} /> Filtry {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                </button>
            </div>

            {/* Active filter chips */}
            {hasFilters && (
                <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100" data-testid="active-filters">
                    {selectedBrands.map(b => (
                        <Badge key={b} variant="secondary" className="flex items-center gap-1 cursor-pointer hover:bg-red-50 hover:text-red-600 text-xs rounded-full" onClick={() => toggleBrand(b)}>
                            <Tag size={9} /> {b} <X size={9} />
                        </Badge>
                    ))}
                    {Object.entries(selectedParams || {}).filter(([, v]) => v).map(([k, v]) => (
                        <Badge key={k} variant="secondary" className="flex items-center gap-1 cursor-pointer hover:bg-red-50 hover:text-red-600 text-xs rounded-full" onClick={() => toggleParam(k, v)}>
                            <Zap size={9} /> {k}: {v} <X size={9} />
                        </Badge>
                    ))}
                    {inStockOnly && (
                        <Badge variant="secondary" className="flex items-center gap-1 cursor-pointer text-xs hover:bg-red-50 hover:text-red-600 rounded-full" onClick={() => setInStockOnly(false)}>
                            <Truck size={9} /> Skladem <X size={9} />
                        </Badge>
                    )}
                    {onSaleOnly && (
                        <Badge className="flex items-center gap-1 cursor-pointer text-xs bg-red-50 text-red-600 border-red-200 hover:bg-red-100 rounded-full" onClick={() => setOnSaleOnly(false)}>
                            Ve slevě <X size={9} />
                        </Badge>
                    )}
                    {(priceRange[0] > 0 || priceRange[1] < 200000) && (
                        <Badge variant="secondary" className="flex items-center gap-1 cursor-pointer text-xs hover:bg-red-50 hover:text-red-600 rounded-full" onClick={() => setPriceRange([0, 200000])}>
                            {priceRange[0].toLocaleString("cs-CZ")}-{priceRange[1] >= 200000 ? "max" : priceRange[1].toLocaleString("cs-CZ")} Kč <X size={9} />
                        </Badge>
                    )}
                    <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 px-1.5 transition-colors">
                        <RotateCcw size={10} /> Vše smazat
                    </button>
                    {isLoggedIn && (
                        <button onClick={() => setShowSaveFilter(true)} className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 px-1.5 transition-colors ml-2" data-testid="save-filter-btn">
                            <BookmarkPlus size={10} /> Uložit filtr
                        </button>
                    )}
                </div>
            )}

            {isLoggedIn && savedFilters.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100" data-testid="saved-filters">
                    <span className="text-[10px] text-gray-400 font-medium self-center mr-1"><Heart size={10} className="inline mr-0.5" />Moje filtry:</span>
                    {savedFilters.map(f => (
                        <div key={f.id} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2.5 py-1 text-[11px] font-medium">
                            <button onClick={() => handleLoadFilter(f)} className="hover:underline" data-testid={`load-filter-${f.id}`}>{f.name}</button>
                            <button onClick={() => handleDeleteFilter(f.id)} className="text-purple-400 hover:text-red-500 ml-0.5"><X size={10} /></button>
                        </div>
                    ))}
                </div>
            )}

            {showSaveFilter && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                    <Input value={saveFilterName} onChange={e => setSaveFilterName(e.target.value)}
                        placeholder="Nazev filtru..." className="h-8 text-sm flex-1 max-w-xs" autoFocus
                        onKeyDown={e => e.key === "Enter" && handleSaveFilter()}
                        data-testid="save-filter-name-input" />
                    <Button size="sm" onClick={handleSaveFilter} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="save-filter-confirm-btn">Uložit</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowSaveFilter(false)} className="h-8 text-xs">Zrušit</Button>
                </div>
            )}
        </div>
    );
}

export function FeaturedProducts({ bestsellers }) {
    return (
        <div className="mb-6" data-testid="featured-products-panel">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Doporučené produkty</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {bestsellers.slice(0, 5).map(p => (
                    <Link key={p.id} to={`/produkt/${p.slug}`}
                        className="group bg-white rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-md transition-all p-3 text-center"
                        data-testid={`featured-product-${p.slug}`}>
                        <div className="w-full aspect-square rounded-lg bg-gray-50 overflow-hidden mb-2 flex items-center justify-center">
                            {p.main_image ? (
                                <img src={p.main_image} alt={p.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform" loading="lazy" />
                            ) : (
                                <Package size={24} className="text-gray-300" />
                            )}
                        </div>
                        <p className="text-xs font-semibold text-gray-700 group-hover:text-green-700 line-clamp-2 leading-tight mb-1">{p.name}</p>
                        <p className="text-sm font-bold" style={{ color: "var(--sg-primary)" }}>
                            {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kč
                        </p>
                    </Link>
                ))}
            </div>
        </div>
    );
}
